import { useEffect } from "react";
import styled from "styled-components";
import shortid from "shortid";

import { scrollWindowTo } from "helpers";
import WP from "WP";

import Image from "partials/Image";
import GridElement from "partials/GridElement";
import Container from "partials/Container";
import LoadingButton from "partials/LoadingButton";
import Button from "partials/Button";
import Html from "partials/Html";
import Input from "partials/InputCounter";
import IconSelector from "partials/IconSelector";

import Ticket from "icons/TicketIcon";
import Media from "css/Media";

const MobileRoomTypeCard = ({
  room,
  disabled,
  open,
  numRooms,
  numPersons,
  onGalleryClick,
  handleInfoClick,
  handleTermsClick,
  handleChange,
  handleSubmit,
  availability,
  getPrice,
  submitting,
}) => {
  const infoTabId = "infoTab-" + shortid.generate();

  useEffect(() => {
    if (open) {
      scrollToInfo();
    }
  }, [open]);

  const scrollToInfo = () => {
    let id = document.getElementById(infoTabId);
    scrollWindowTo(id, 500, "easeInOutCubic");
  };

  return (
    <Wrapper disabled={disabled}>
      <Container grid>
        <GridElement colStart={1} colEnd={8} rowStart={1}>
          <StyledImage
            image={room.featured_image}
            ratio='5:4.4'
            background
            onClick={() => onGalleryClick(room.image_gallery)}
          >
            <GalleryPopUp>
              <IconSelector icon='gallery' negative />
              <p>{`+ ${room.image_gallery.length}`}</p>
            </GalleryPopUp>
          </StyledImage>
        </GridElement>
        <GridElement colStart={1} colEnd={8} rowStart={2}>
          <h3>{room.room_name}</h3>
        </GridElement>
        <GridElement colStart={1} colEnd={8} rowStart={3}>
          <InfoHtml content>{room.rich_text[0].text_field}</InfoHtml>
        </GridElement>
        <GridElement colStart={1} colEnd={8} rowStart={4}>
          <InfoButton onClick={handleInfoClick} open={open}>
            <p>{open ? WP.trans.lessInfo : WP.trans.moreInfo}</p>
          </InfoButton>
        </GridElement>
        {!disabled && (
          <>
            <GridElement colStart={1} colEnd={4} rowStart={5}>
              <StyledInput
                name='numRooms'
                handleChange={(name, value) => handleChange(name, value)}
                value={numRooms}
                unit={WP.trans.rooms}
                green
              />
            </GridElement>
            <GridElement colStart={5} colEnd={8} rowStart={5}>
              <StyledInput
                name='numPersons'
                handleChange={(name, value) => handleChange(name, value)}
                value={numPersons}
                unit={WP.trans.guestsPerRoom}
                green
                isRightAligned
              />
            </GridElement>
          </>
        )}
        {!disabled && (
          <GridElement colStart={1} colEnd={4} rowStart={6} align='center'>
            <PriceInfo>
              <p>
                NOK{" "}
                {availability.hotels.map((roomType) => {
                  return roomType.id == room.visbook_room_id
                    ? getPrice(roomType)
                    : null;
                })}
              </p>
            </PriceInfo>
          </GridElement>
        )}
        <GridElement colStart={5} colEnd={8} rowStart={6}>
          {submitting ? (
            <StyledLoadingButton placeholder={WP.trans.order} />
          ) : (
            <StyledButton
              disabled={disabled}
              onClick={disabled ? null : handleSubmit}
            >
              {disabled ? WP.trans.noAvailableRooms : WP.trans.order}
            </StyledButton>
          )}
        </GridElement>
      </Container>
      {open && (
        <Container grid mobileBleed id={infoTabId}>
          <GridElement colStart={1} colEnd={8}>
            <InfoPopUp disabled={disabled}>
              <InnerGrid>
                <GridElement colStart={1} colEnd={8} rowStart={1}>
                  <StyledHtml content right>
                    {room.rich_text[1].text_field}
                  </StyledHtml>
                </GridElement>
                <GridElement colStart={1} colEnd={8} rowStart={2}>
                  <TermsPopUp onClick={handleTermsClick}>
                    <Ticket />
                    <p>{WP.trans.orderAndTerms}</p>
                  </TermsPopUp>
                </GridElement>
              </InnerGrid>
            </InfoPopUp>
          </GridElement>
        </Container>
      )}
    </Wrapper>
  );
};

export default MobileRoomTypeCard;

const Wrapper = styled.div`
  display: block;
  width: 100%;

  .container {
    background-color: ${(props) =>
      props.disabled ? props.theme.colors.disabled : props.theme.colors.bg};
    padding-top: ${(props) => props.theme.spacing.contentL};

    &:first-of-type {
      padding-bottom: ${(props) => props.theme.spacing.elementM};
    }

    h3 {
      font-weight: 600;
      padding: ${(props) => `${props.theme.spacing.contentL} 0
        ${props.theme.spacing.contentM}`};
    }
  }
  ${Media.greaterThan("m")`
      display: none;
  `}
`;

const StyledImage = styled(Image)`
  position: relative;
  cursor: pointer;
`;

const GalleryPopUp = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: ${(props) => props.theme.spacing.contentM} 25px;
  z-index: 1;
  display: flex;
  color: ${(props) => props.theme.colors.negative};

  > span {
    margin-right: ${(props) => props.theme.spacing.contentXS};
  }

  p {
    text-decoration: underline;
  }
`;

const InfoHtml = styled(Html)`
  padding-bottom: ${(props) => props.theme.spacing.contentM};
`;

const InfoButton = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.contentXL};
  margin: ${(props) =>
    props.open
      ? `${(props) => props.theme.spacing.contentXL} 0 ${(props) =>
          props.theme.spacing.contentXS}`
      : `${(props) => props.theme.spacing.contentXL} 0`};
  user-select: none;

  p {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const StyledInput = styled(Input)`
  margin-bottom: ${(props) => props.theme.spacing.contentL};
  padding: 0;
`;

const PriceInfo = styled.div`
  p {
    text-align: center;
    font-size: 18px;
    font-weight: 600;
    line-height: 23px;
  }
`;

const StyledButton = styled(Button)`
  padding: ${(props) => props.theme.spacing.contentM} 0;
`;

const StyledLoadingButton = styled(LoadingButton)`
  padding: ${(props) => props.theme.spacing.contentM} 0;
`;

const InfoPopUp = styled.div`
  padding: 25px;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.disabled : props.theme.colors.bgDark};
`;

const InnerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr 0.359fr) 1fr;
  grid-template-rows: ${(props) => props.rows || "auto"};
`;

const StyledHtml = styled(Html)`
  padding-bottom: ${(props) => props.theme.spacing.contentXL};
`;

const TermsPopUp = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  svg {
    margin-right: ${(props) => props.theme.spacing.contentXS};
  }

  p {
    text-decoration: underline;
  }
`;
